import { React } from 'react';
import {
  LegendContainer,
  LabelStyle,
  LegendTextStyle,
  RowStyle,
  LegendTextCollapsStyle,
  LabelCollapsedStyle,
  LegendCollapsContainer,
  StyledSpanContainer,
  NoWrapSpan,
} from '@atoms/sidebar/sidebar.styles';

import { formatNumberWithKAndM, getAccuracy, formatVarianceWithKAndM } from '@util/commonUtil';
import { useRecoilValue } from 'recoil';
import { Recoil_Quarter_State, SidebarExpandCollapseState, Current_User } from '@recoil/recoil';
import {
  QUARTERLY_DETAILS,
  MONTH_QUARTER_STATUS,
  PERIOD,
  FORECAST_SIDEBAR_QUARTER_CONST,
} from '@constants/commonConstants';
import { MissedText } from '../../../atoms/dashboard/quarterlyDetails.styles';

const Legend = ({ quarterlyData, quarterText, quarterTitle, auditRiskData }) => {
  const quarterInfo = useRecoilValue(Recoil_Quarter_State);
  const sidebarExpandState = useRecoilValue(SidebarExpandCollapseState);
  const currentUser = useRecoilValue(Current_User);
  const pageUrl = window.location.pathname;
  const isForecastPage = pageUrl.includes('/forecast');
  const isCurrentQuarterSelection =
    quarterText === quarterInfo.quarter && quarterlyData?.selected_year === quarterInfo.fiscal_year;

  const data = quarterlyData;
  const wonValue = quarterlyData?.sold;
  let forecastedValue = quarterlyData?.forecast;

  // Function to get audit risk value for the current quarter
  const getAuditRiskValue = () => {
    if (
      !auditRiskData ||
      !quarterText ||
      (currentUser?.role !== 'MSL' && currentUser?.role !== 'GCSP')
    ) {
      return 0;
    }

    // Map quarter text to audit risk data format
    const quarterMap = {
      Q1: 'q1',
      Q2: 'q2',
      Q3: 'q3',
      Q4: 'q4',
    };

    const mappedQuarter = quarterMap[quarterText];
    const auditRiskItem = auditRiskData.find(item => item?.quarter === mappedQuarter);
    return auditRiskItem ? auditRiskItem?.value : 0;
  };

  // Calculate enhanced forecast value for MSL and GCSP users
  const getEnhancedForecastValue = () => {
    if (currentUser?.role === 'MSL' || currentUser?.role === 'GCSP') {
      const auditRiskValue = getAuditRiskValue();
      return (forecastedValue || 0) + auditRiskValue;
    }
    return forecastedValue;
  };

  let accuracy;
  if (quarterlyData.period === 'future') {
    accuracy = QUARTERLY_DETAILS.TBD;
  } else if (quarterlyData.period !== 'future') {
    // Use enhanced forecast value for MSL and GCSP users in accuracy calculation
    const forecastForAccuracy =
      currentUser?.role === 'MSL' || currentUser?.role === 'GCSP'
        ? getEnhancedForecastValue()
        : forecastedValue;
    accuracy =
      quarterlyData?.tag === MONTH_QUARTER_STATUS.NOT_APPLICABLE
        ? QUARTERLY_DETAILS.NOT_APPLICABLE
        : `${getAccuracy(wonValue, forecastForAccuracy, quarterlyData?.tag) || 0}%`;
  } else accuracy = null; //eslint-disable-line

  // here forecasted value in -ve case was not handled go gap value was not being updated
  if (forecastedValue < 0) forecastedValue = 0;

  const getValue = (value, caption) => {
    if (data?.valid_quarter === false) {
      return QUARTERLY_DETAILS.NOT_APPLICABLE;
    } else if (
      (caption === QUARTERLY_DETAILS.GAP || caption === QUARTERLY_DETAILS.FORECAST) &&
      data?.tag === MONTH_QUARTER_STATUS.NOT_APPLICABLE
    ) {
      return QUARTERLY_DETAILS.NOT_APPLICABLE;
    } else {
      return formatNumberWithKAndM(value) || 0;
    }
  };

  const getVarianceValue = () => {
    if (data?.valid_quarter === false || data?.tag === MONTH_QUARTER_STATUS.NOT_APPLICABLE) {
      return QUARTERLY_DETAILS.NOT_APPLICABLE;
    } else {
      // For MSL and GCSP users, use enhanced forecast value (forecast + audit risk) for variance calculation
      const forecastForVariance =
        currentUser?.role === 'MSL' || currentUser?.role === 'GCSP'
          ? getEnhancedForecastValue()
          : forecastedValue;
      return formatVarianceWithKAndM(wonValue - forecastForVariance) || 0;
    }
  };

  // Enhanced getValue function that includes audit risk for MSL forecast
  const getEnhancedValue = (value, caption) => {
    const baseValue = getValue(value, caption);

    // Add audit risk value to forecast for MSL and GCSP users
    if (
      caption === QUARTERLY_DETAILS.FORECAST &&
      (currentUser?.role === 'MSL' || currentUser?.role === 'GCSP')
    ) {
      if (baseValue === QUARTERLY_DETAILS.NOT_APPLICABLE) {
        return baseValue;
      }

      const auditRiskValue = getAuditRiskValue();
      const enhancedValue = (value || 0) + auditRiskValue;
      return formatNumberWithKAndM(enhancedValue) || 0;
    }

    return baseValue;
  };

  return (
    <>
      {data ? (
        <>
          {sidebarExpandState ? (
            <LegendContainer aria-live="polite">
              {/* Forecast */}
              <LabelStyle>
                {isForecastPage ? (
                  <>
                    <NoWrapSpan>{getValue(forecastedValue, QUARTERLY_DETAILS.FORECAST)}</NoWrapSpan>
                    {/* {data?.tag === 'missed' && <MissedText>(Missed M)</MissedText>} */}
                  </>
                ) : (
                  <StyledSpanContainer>
                    {getEnhancedValue(forecastedValue, QUARTERLY_DETAILS.FORECAST)}
                  </StyledSpanContainer>
                )}
                <RowStyle>
                  <LegendTextStyle>{QUARTERLY_DETAILS.FORECAST}</LegendTextStyle>
                  {data?.tag === 'missed' && data?.actual_period !== 'current' && (
                    <MissedText>(Missed)</MissedText>
                  )}
                </RowStyle>
              </LabelStyle>

              {/* Won */}
              <LabelStyle>
                {isForecastPage ? (
                  <NoWrapSpan>{getValue(wonValue, QUARTERLY_DETAILS.WON)}</NoWrapSpan>
                ) : (
                  <StyledSpanContainer>
                    {getValue(wonValue, QUARTERLY_DETAILS.WON)}
                  </StyledSpanContainer>
                )}
                <RowStyle>
                  <LegendTextStyle>{QUARTERLY_DETAILS.WON}</LegendTextStyle>
                </RowStyle>
              </LabelStyle>

              {/* Variance */}
              <LabelStyle>
                {isForecastPage ? (
                  <NoWrapSpan>{getVarianceValue()}</NoWrapSpan>
                ) : (
                  <StyledSpanContainer>{getVarianceValue()}</StyledSpanContainer>
                )}
                <RowStyle>
                  <LegendTextStyle>{QUARTERLY_DETAILS.VARIANCE}</LegendTextStyle>
                </RowStyle>
              </LabelStyle>

              {!isForecastPage && (
                <>
                  <LabelStyle>
                    <StyledSpanContainer>{accuracy}</StyledSpanContainer>

                    <RowStyle>
                      <LegendTextStyle>{QUARTERLY_DETAILS.ACCURACY}</LegendTextStyle>
                    </RowStyle>
                  </LabelStyle>

                  {currentUser?.role !== 'MSL' && (
                    <LabelStyle>
                      <StyledSpanContainer>
                        {formatNumberWithKAndM(data?.noCommit)}
                      </StyledSpanContainer>
                      <RowStyle>
                        <LegendTextStyle>{QUARTERLY_DETAILS.UNCOMMITED_PIPELINE}</LegendTextStyle>
                      </RowStyle>
                    </LabelStyle>
                  )}
                </>
              )}
            </LegendContainer>
          ) : (
            <LegendCollapsContainer>
              {data.period !== PERIOD.PAST ||
                (quarterText === quarterInfo.quarter &&
                  data?.selected_year === quarterInfo.fiscal_year && (
                    <LabelCollapsedStyle>
                      <RowStyle>
                        <LegendTextCollapsStyle>
                          {pageUrl === '/forecast'
                            ? QUARTERLY_DETAILS.CURRENT_COMMIT
                            : QUARTERLY_DETAILS.FORECAST}
                        </LegendTextCollapsStyle>
                      </RowStyle>
                      <NoWrapSpan>{getValue(data?.commit, QUARTERLY_DETAILS.COMMIT)}</NoWrapSpan>
                    </LabelCollapsedStyle>
                  ))}

              {data.period !== PERIOD.PAST ||
                (quarterText === quarterInfo.quarter &&
                  data?.selected_year === quarterInfo.fiscal_year && (
                    <LabelCollapsedStyle>
                      <RowStyle>
                        <LegendTextCollapsStyle>
                          {QUARTERLY_DETAILS.UNCOMMITED_PIPELINE}
                        </LegendTextCollapsStyle>
                      </RowStyle>
                      <NoWrapSpan>{formatNumberWithKAndM(data?.noCommit) || 0}</NoWrapSpan>{' '}
                    </LabelCollapsedStyle>
                  ))}

              {/* Next */}
              {quarterTitle === FORECAST_SIDEBAR_QUARTER_CONST.NEXT ? (
                <LabelCollapsedStyle>
                  <RowStyle>
                    <LegendTextCollapsStyle>
                      {QUARTERLY_DETAILS.PROJECTED_FORECAST}
                    </LegendTextCollapsStyle>
                  </RowStyle>
                  <NoWrapSpan>{formatNumberWithKAndM(data?.commit) || 0}</NoWrapSpan>
                </LabelCollapsedStyle>
              ) : null}

              {quarterTitle === FORECAST_SIDEBAR_QUARTER_CONST.NEXT ? (
                <LabelCollapsedStyle>
                  <RowStyle>
                    <LegendTextCollapsStyle>
                      {QUARTERLY_DETAILS.UNCOMMITED_PIPELINE}
                    </LegendTextCollapsStyle>
                  </RowStyle>
                  <NoWrapSpan>{formatNumberWithKAndM(data?.noCommit) || 0}</NoWrapSpan>
                </LabelCollapsedStyle>
              ) : null}

              {data.actual_period === PERIOD.PAST && !isCurrentQuarterSelection && (
                <LabelCollapsedStyle>
                  <RowStyle>
                    <LegendTextCollapsStyle>{QUARTERLY_DETAILS.FORECAST}</LegendTextCollapsStyle>
                  </RowStyle>
                  <NoWrapSpan>{getValue(forecastedValue, QUARTERLY_DETAILS.FORECAST)}</NoWrapSpan>
                </LabelCollapsedStyle>
              )}

              {data.actual_period === PERIOD.PAST && !isCurrentQuarterSelection && (
                <LabelCollapsedStyle>
                  <RowStyle>
                    <LegendTextCollapsStyle>
                      {QUARTERLY_DETAILS.UNCOMMITED_PIPELINE}
                    </LegendTextCollapsStyle>
                  </RowStyle>
                  <NoWrapSpan>{formatNumberWithKAndM(data?.noCommit) || 0}</NoWrapSpan>{' '}
                </LabelCollapsedStyle>
              )}
            </LegendCollapsContainer>
          )}
        </>
      ) : null}
    </>
  );
};

export default Legend;
